import React, { FormEvent, useMemo, useState, ReactNode } from "react";
import LoggedOutPage from "./LoggedOutPage";
import Countdown from "./Countdown";
import Loading from "./Loading";
import { Link, useSearchParams } from "react-router-dom";
import { IfComp } from "@dvrt/react-tools";
import { findErrors } from "../utils/passwordTools";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import { useUserData } from "../context/UserDataProvider";
import { useFetch } from "./FetchProvider";
import {
    Box,
    Button,
    FormControlLabel,
    Switch,
    Stack,
    TextField,
    Typography,
    Alert,
    Dialog,
    DialogContent,
} from "@mui/material";
import PasswordField from "./PasswordField";

const rememberStorageKey = "remember-checkbox-value";

type ServerError =
    | "unexpected_error"
    | "invalid_credentials"
    | "too_many_attempts_notification"
    | "too_many_totp_attempts"
    | "invalid_flow";

const serverErrorTranslations: Record<ServerError, MessageDescriptor> = {
    unexpected_error: {
        id: "login.unexpected_error",
        defaultMessage: "Unexpected error when contacting the server. Please, try again!",
    },
    invalid_credentials: {
        id: "login.invalid_credentials",
        defaultMessage: "Invalid credentials",
    },
    too_many_totp_attempts: {
        id: "login.too_many_totp_attempts",
        defaultMessage: "Too many incorrect authentication codes. Start a new login to try again.",
    },
    invalid_flow: {
        id: "login.invalid_flow",
        defaultMessage: "This login has expired. Please, start the login again.",
    },
    too_many_attempts_notification: {
        id: "login.too_many_attempts_notification",
        defaultMessage: "Too many login attempts",
    },
};

type LoginSuccess = {
    result:
        | "success"
        | "totp_code_needed"
        | "invalid_totp_code"
        | "password_change_needed"
        | "missing_client_certificate"
        | "restart_login";
    redirect_to?: string; /// only set in case of success, missing_client_certificate and restart_login
};

type PasswordChangeNeededResponse = {
    result: "password_change_needed";
    mandatory: boolean;
};

type LoginFailure = {
    result: "invalid_credentials" | "too_many_login_attempts" | "current_password_mismatch" | "invalid_flow";
    timeout?: number;
};

type PasswordChangeFailure = {
    result: "current_password_mismatch" | "invalid_password_strength" | "invalid_flow";
} & PasswordStrengthRequirementResponse;

/** The JSON body of an error response, or null for one without it - an error page of a proxy, for example */
async function readErrorBody<T>(resp: Response): Promise<T | null> {
    try {
        return await resp.json();
    } catch {
        return null;
    }
}

type Stage =
    | { stage: "start" }
    | { stage: "totp"; invalid_code: boolean }
    | { stage: "password_change"; mandatory: boolean };

type PasswordChangeData =
    | {
          currentPassword: string;
          newPassword: string;
      }
    | "postpone";

type PasswordStrengthRequirementField = "min_len" | "min_lowercase" | "min_uppercase" | "min_digits" | "min_special";

type PasswordStrengthRequirementResponse = {
    [key in PasswordStrengthRequirementField]?: number;
};
type LoginFormData = {
    username: string;
    password: string;
    remember: boolean;
};

const LoginForm = ({
    onSubmit,
    state,
    setState,
    serverError,
    focus,
}: {
    onSubmit: () => void;
    state: LoginFormData;
    setState: (values: Partial<LoginFormData>) => void;
    serverError: string | null;
    focus: "username" | "password";
}) => {
    return (
        <Box
            component="form"
            noValidate
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
                event.stopPropagation();
                localStorage.setItem(rememberStorageKey, JSON.stringify(state.remember));
                onSubmit();
            }}
            sx={{
                minWidth: "25ch", // limit form width to 25 characters
                maxWidth: "30ch", // ensure texts wrap for better layout
            }}
        >
            <Stack spacing={2}>
                <TextField
                    label={<FormattedMessage id="login.username" defaultMessage="Login name" />}
                    variant="outlined"
                    type="text"
                    value={state.username}
                    onChange={(ev) => setState({ username: ev.target.value })}
                    required
                    fullWidth
                    autoFocus={focus === "username"}
                    autoComplete="username"
                    name="username"
                />

                <PasswordField
                    label={defineMessage({
                        id: "login.password",
                        defaultMessage: "Password",
                    })}
                    value={state.password}
                    onChange={(ev) => setState({ password: ev.target.value })}
                    autoComplete="current-password"
                    name="password"
                    autoFocus={focus === "password"}
                />

                {serverError && (
                    <Alert severity="error">
                        <FormattedMessage {...serverErrorTranslations[serverError]} />
                    </Alert>
                )}

                <IfComp condition={window.features.rememberMe}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={state.remember}
                                onChange={(ev) => setState({ remember: ev.target.checked })}
                            />
                        }
                        label={<FormattedMessage id="login.remember" defaultMessage="Remember me" />}
                        labelPlacement="start"
                    />
                </IfComp>

                <Button type="submit" variant="contained" color="primary">
                    <FormattedMessage id="submit" defaultMessage="Submit" />
                </Button>

                <Link to="/public/request-password-reset">
                    <FormattedMessage id="reset_password.login_form_link" defaultMessage="Forgot your password?" />
                </Link>
            </Stack>
        </Box>
    );
};

type PasswordChangeFormState = {
    currentPassword: string;
    newPassword: string;
    retypePassword: string;
};

function errorList(errors: MessageDescriptor[]) {
    return (
        <ul>
            {errors.map((error) => (
                <li>
                    <FormattedMessage {...error} />
                </li>
            ))}
        </ul>
    );
}

const PasswordChangeForm = ({
    onSubmit,
    mandatory,
    currentPasswordMismatch,
    serverError,
}: {
    onSubmit: (data: PasswordChangeData) => Promise<void>;
    mandatory: boolean;
    currentPasswordMismatch: boolean;
    serverError: ServerError | null;
}) => {
    const [state, _setState] = useState<PasswordChangeFormState>({
        currentPassword: "",
        newPassword: "",
        retypePassword: "",
    });

    const setState = (values: Partial<PasswordChangeFormState>) => {
        _setState((prev) => ({ ...prev, ...values }));
    };

    const newPasswordValidationErrors = useMemo(() => {
        const strengthConfig = window.passwordStrength;
        if (strengthConfig === undefined) return [];

        return findErrors(strengthConfig, state.newPassword);
    }, [state.newPassword]);

    return (
        <Box
            component="form"
            noValidate
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onSubmit({ currentPassword: state.currentPassword, newPassword: state.newPassword });
            }}
            sx={{
                width: "30ch",
            }}
        >
            <Stack spacing={2}>
                <PasswordField
                    label={defineMessage({
                        id: "change_password.current_password",
                        defaultMessage: "Current password",
                    })}
                    value={state.currentPassword}
                    onChange={(ev) => setState({ currentPassword: ev.target.value })}
                    autoComplete="current-password"
                    error={currentPasswordMismatch}
                    helperText={
                        currentPasswordMismatch ? (
                            <FormattedMessage
                                id="current_password_does_not_match"
                                defaultMessage="The provided password does not match the current password"
                            />
                        ) : (
                            ""
                        )
                    }
                    name="current_password"
                />

                <PasswordField
                    label={defineMessage({ id: "change_password.new_password", defaultMessage: "New password" })}
                    value={state.newPassword}
                    onChange={(ev) => setState({ newPassword: ev.target.value })}
                    autoComplete="new-password"
                    error={state.newPassword && newPasswordValidationErrors.length > 0}
                    helperText={
                        state.newPassword && newPasswordValidationErrors.length
                            ? errorList(newPasswordValidationErrors)
                            : null
                    }
                    name="new_password"
                />

                <PasswordField
                    label={defineMessage({ id: "change_password.retype_password", defaultMessage: "Retype password" })}
                    value={state.retypePassword}
                    onChange={(ev) => setState({ retypePassword: ev.target.value })}
                    autoComplete="new-password"
                    error={state.newPassword != state.retypePassword}
                    helperText={state.newPassword != state.retypePassword ? "NA" : null}
                    name="retype_password"
                />

                {serverError && (
                    <Alert severity="error">
                        <FormattedMessage {...serverErrorTranslations[serverError]} />
                    </Alert>
                )}

                <Stack direction="row" spacing={1} justifyContent="stretch" sx={{ width: "100%" }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={
                            state.currentPassword.length === 0 ||
                            state.newPassword.length === 0 ||
                            newPasswordValidationErrors.length > 0 ||
                            state.newPassword !== state.retypePassword
                        }
                        sx={{ flex: 1 }}
                    >
                        <FormattedMessage id="submit" defaultMessage="Submit" />
                    </Button>
                    <IfComp condition={!mandatory}>
                        <Button
                            name="postpone"
                            type="button" // if type is omitted it is treated as 'submit'
                            variant="contained"
                            color="secondary"
                            onClick={() => onSubmit("postpone")}
                            sx={{ flex: 1 }}
                        >
                            <FormattedMessage id="postpone" defaultMessage="Postpone" />
                        </Button>
                    </IfComp>
                </Stack>
            </Stack>
        </Box>
    );
};

const TOTPForm = ({
    onSubmit,
    hasError,
    serverError,
}: {
    hasError: boolean;
    onSubmit: (code: string) => void;
    serverError: string | null;
}) => {
    const [totpCode, setTotpCode] = useState<string>("");

    return (
        <Box
            component="form"
            noValidate
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onSubmit(totpCode);
            }}
            sx={{
                minWidth: "25ch", // minimum width to 25 characters,
                maxWidth: "30ch", // ensure texts wrap for better layout
            }}
        >
            <Stack spacing={2}>
                {serverError && (
                    <Alert severity="error">
                        <FormattedMessage {...serverErrorTranslations[serverError]} />
                    </Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                        id="account.totp.verify.description"
                        defaultMessage="Open the two-factor authenticator (TOTP) app on your mobile device to view your authentication code."
                    />
                </Typography>

                <TextField
                    label={<FormattedMessage id="account.totp.verify.title" defaultMessage="Authentication code" />}
                    variant="outlined"
                    type="text"
                    value={totpCode}
                    onChange={(ev) => setTotpCode(ev.target.value)}
                    required
                    autoFocus={true}
                    slotProps={{
                        input: {
                            inputProps: {
                                maxLength: 6,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                            },
                        },
                    }}
                    autoComplete="one-time-code"
                    error={hasError}
                    helperText={
                        hasError ? (
                            <FormattedMessage
                                id="account.totp.generate_stage.code_verification.error.invalid_code"
                                defaultMessage="Invalid code"
                            />
                        ) : (
                            ""
                        )
                    }
                />

                <Button disabled={totpCode.length !== 6} variant="contained" color="primary" type="submit">
                    <FormattedMessage id="submit" defaultMessage="Submit" />
                </Button>
            </Stack>
        </Box>
    );
};

/**
 * Login component
 */
const Login = () => {
    const [serverError, setServerError] = useState<ServerError | null>(null);
    const [tooManyAttemptsTimeout, setTooManyAttemptsTimeout] = useState<number>(null);
    const [displayLoader, setDisplayLoader] = useState<boolean>(false);
    const [stage, setStage] = useState<Stage>({ stage: "start" });
    const userData = useUserData();
    const fetch = useFetch();
    const [searchParams] = useSearchParams();
    const flowId = searchParams.get("flow");
    const loginHint = searchParams.get("login_hint");

    // To make it more convenient for the user, if login hint is provided, the form should focus
    // the password instead of the username field.
    const loginFieldFocus = loginHint ? "password" : "username";

    // We have to declare it here, otherwise if we try to catch the error in the child component,
    // since we display a loader instead of the change password form component, React will throw a
    // 'Can't perform a React state update on an unmounted component' error.
    // So we need to pass the error through the parent component.
    const [passwordChangeCurrentMismatch, setPasswordChangeCurrentMismatch] = useState<boolean>(false);

    // State is stored here, because due to conditional rendering, even if we
    // hold the LoginForm in a useMemo or useCallback, the component would
    // unmount, causing the useStates to be reset inside the LoginForm.
    const [loginFormState, setLoginFormState] = useState<LoginFormData>({
        username: loginHint || "",
        password: "",
        remember: window.features.rememberMe && JSON.parse(localStorage.getItem(rememberStorageKey) || "false"),
    });

    function loginSuccess(response: LoginSuccess) {
        console.debug("Login success");
        const destination = new URL(response.redirect_to, window.location.origin);
        if (destination.protocol !== "http:" && destination.protocol !== "https:") {
            console.error("Refusing an unsafe login redirect");
            window.location.replace("/");
            return;
        }
        window.location.href = destination.href;
    }

    async function _onSubmit() {
        console.debug("onSubmit");
        setDisplayLoader(true);
        setServerError(null);
        const resp = await fetch("/openid-provider/authorization-api", {
            method: "POST",
            body: JSON.stringify({
                username: loginFormState.username,
                password: loginFormState.password,
                remember: loginFormState.remember,
                flow: flowId,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            const response: LoginSuccess = await resp.json();
            switch (response.result) {
                case "success":
                    loginSuccess(response);
                    return;
                case "totp_code_needed":
                    setStage({ stage: "totp", invalid_code: false });
                    break;
                case "password_change_needed":
                    setStage({
                        stage: "password_change",
                        mandatory: (response as PasswordChangeNeededResponse).mandatory,
                    });
                    break;
                case "missing_client_certificate":
                    window.location.href = response.redirect_to;
                    break;
                case "restart_login":
                    window.location.href = response.redirect_to;
                    return;
                default:
                    setServerError("unexpected_error");
            }
        } else {
            const response = await readErrorBody<LoginFailure>(resp);

            const setTooManyAttemptsTimeoutAndReset = (timeout: number) => {
                setTooManyAttemptsTimeout(timeout);
                setTimeout(() => {
                    setTooManyAttemptsTimeout(null);
                }, timeout * 1000);
            };

            switch (response?.result) {
                case "invalid_credentials":
                    setServerError("invalid_credentials");
                    if (response.timeout) {
                        setTooManyAttemptsTimeoutAndReset(response.timeout);
                    }
                    break;
                case "too_many_login_attempts":
                    setServerError("too_many_attempts_notification");
                    setTooManyAttemptsTimeoutAndReset(response.timeout);
                    break;
                case "invalid_flow":
                    setServerError("invalid_flow");
                    break;
                default:
                    setServerError("unexpected_error");
            }
        }
        setDisplayLoader(false);
    }

    async function _onTotpSubmit(code: string) {
        console.debug("onTotpSubmit");
        setDisplayLoader(true);
        setServerError(null);
        const resp = await fetch("/openid-provider/verify-totp-code", {
            method: "POST",
            body: JSON.stringify({ code, flow: flowId }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            const response: LoginSuccess = await resp.json();
            switch (response.result) {
                case "success":
                    loginSuccess(response);
                    return;
                case "invalid_totp_code":
                    setStage({ stage: "totp", invalid_code: true });
                    break;
                case "password_change_needed":
                    setStage({
                        stage: "password_change",
                        mandatory: (response as PasswordChangeNeededResponse).mandatory,
                    });
                    break;
            }
        } else {
            const response = await readErrorBody<{ result?: string }>(resp);
            switch (response?.result) {
                case "too_many_totp_attempts":
                    setServerError("too_many_totp_attempts");
                    break;
                case "invalid_flow":
                    setServerError("invalid_flow");
                    break;
                default:
                    setServerError("unexpected_error");
            }
        }
        setDisplayLoader(false);
    }

    async function _submitPasswordChange(data: PasswordChangeData) {
        setPasswordChangeCurrentMismatch(false);
        let body: Record<string, any>;
        if (data === "postpone") {
            console.debug("postponing password change");
            body = { postpone: true };
        } else {
            console.debug("submit password change");
            body = {
                current_password: data.currentPassword,
                new_password: data.newPassword,
            };
        }
        body["flow"] = flowId;

        setDisplayLoader(true);
        setServerError(null);
        const resp = await fetch("/openid-provider/change-password", {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            const response: LoginSuccess = await resp.json();
            if (response.result == "success") {
                loginSuccess(response);
                return;
            } else setServerError("unexpected_error");
        } else {
            const response = await readErrorBody<PasswordChangeFailure>(resp);

            if (response?.result === "current_password_mismatch") {
                setPasswordChangeCurrentMismatch(true);
            } else if (response?.result === "invalid_flow") {
                setServerError("invalid_flow");
            } else setServerError("unexpected_error");
        }
        setDisplayLoader(false);
    }

    if (userData) {
        return (
            <LoggedOutPage title={<FormattedMessage id="login.page_title" defaultMessage="Login" />}>
                <Stack>
                    <Box sx={{ paddingRight: 5 }}>
                        <FormattedMessage id="login.already_logged_in" defaultMessage="You are already logged in." />
                    </Box>
                    <a href="/">
                        <FormattedMessage id="login.go_home" defaultMessage="Heimdall home page" />
                    </a>
                </Stack>
            </LoggedOutPage>
        );
    }

    let content: ReactNode;

    if (stage.stage === "totp") {
        content = <TOTPForm hasError={stage.invalid_code} onSubmit={_onTotpSubmit} serverError={serverError} />;
    } else if (stage.stage === "password_change") {
        // stage is password change
        content = (
            <PasswordChangeForm
                onSubmit={_submitPasswordChange}
                mandatory={stage.mandatory}
                currentPasswordMismatch={passwordChangeCurrentMismatch}
                serverError={serverError}
            />
        );
    } else {
        content = (
            <LoginForm
                onSubmit={_onSubmit}
                state={loginFormState}
                setState={(values) => setLoginFormState((prev) => ({ ...prev, ...values }))}
                serverError={serverError}
                focus={loginFieldFocus}
            />
        );
    }

    return (
        <LoggedOutPage title={<FormattedMessage id="login.page_title" defaultMessage="Login" />}>
            {content}

            <Dialog open={tooManyAttemptsTimeout > 0}>
                <DialogContent sx={{ textAlign: "center" }}>
                    <Alert severity="warning">
                        <FormattedMessage
                            id="too_many_attempts_message"
                            defaultMessage="Login is disabled because of too many failed attempts."
                        />
                    </Alert>
                    <Countdown time={tooManyAttemptsTimeout} />
                </DialogContent>
            </Dialog>

            <Dialog open={displayLoader}>
                <DialogContent>
                    <Loading />
                </DialogContent>
            </Dialog>
        </LoggedOutPage>
    );
};

export default Login;
